import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Download, FileText, FileSpreadsheet, File, Loader2 } from 'lucide-react'
import './ReportExport.css'

/**
 * ReportExport Component
 *
 * Dropdown component for exporting reports in various formats.
 *
 * @param {Object} props
 * @param {Function} props.onExport - Export handler (format) => Promise
 * @param {Array} props.formats - Available export formats
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - Button size (small, medium, large)
 */
const ReportExport = ({
	onExport,
	formats = defaultFormats,
	loading = false,
	disabled = false,
	size = 'medium',
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [exportingFormat, setExportingFormat] = useState(null)

	const handleExport = async (format) => {
		if (!onExport) return

		setExportingFormat(format.value)
		try {
			await onExport(format.value)
		} catch (error) {
			console.error('Export failed:', error)
		} finally {
			setExportingFormat(null)
			setIsOpen(false)
		}
	}

	const getIcon = (iconName) => {
		switch (iconName) {
			case 'pdf':
				return <FileText size={16} />
			case 'excel':
				return <FileSpreadsheet size={16} />
			case 'csv':
				return <File size={16} />
			default:
				return <Download size={16} />
		}
	}

	const buttonClasses = [
		'report-export__button',
		`report-export__button--${size}`,
		disabled ? 'report-export__button--disabled' : '',
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className="report-export" onMouseLeave={() => setIsOpen(false)}>
			<button
				className={buttonClasses}
				onClick={() => setIsOpen(!isOpen)}
				disabled={disabled || loading}
				aria-expanded={isOpen}
				aria-haspopup="true"
			>
				{loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
				<span>Export</span>
			</button>

			{isOpen && !disabled && !loading && (
				<div className="report-export__dropdown">
					{formats.map((format) => (
						<button
							key={format.value}
							className="report-export__option"
							onClick={() => handleExport(format)}
							disabled={exportingFormat === format.value}
						>
							{exportingFormat === format.value ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								getIcon(format.icon)
							)}
							<div className="report-export__option-content">
								<span className="report-export__option-label">{format.label}</span>
								{format.description && (
									<span className="report-export__option-description">{format.description}</span>
								)}
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	)
}

// Default export formats
const defaultFormats = [
	{
		value: 'pdf',
		label: 'PDF Document',
		description: 'Best for printing and sharing',
		icon: 'pdf',
	},
	{
		value: 'excel',
		label: 'Excel Spreadsheet',
		description: 'Best for data analysis',
		icon: 'excel',
	},
	{
		value: 'csv',
		label: 'CSV File',
		description: 'Simple data format',
		icon: 'csv',
	},
]

ReportExport.propTypes = {
	onExport: PropTypes.func.isRequired,
	formats: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			description: PropTypes.string,
			icon: PropTypes.string,
		})
	),
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large']),
}

export default ReportExport
